import React from "react";
import { Box, Button, Divider, Stack, Typography } from "@mui/material";
import { overlineSx, sidePanelBandSx, sidePanelSx } from "./cardChrome";
import ListScopeToggle from "./ListScopeToggle";

/**
 * The sticky panel beside a list page (lasers, substrates, camera
 * calibrations, explorations, selections, reproductions), in the shape of
 * the New exploration page's review panel: a dark band with the scope and
 * the count, a short note on what the items are, the list's filters
 * (`filters`, optional) and the page's actions (PanelButton components).
 * `bandSx` overrides the band's look (e.g. a module's colour).
 */
export default function ResourcePanel({ overline, headline, subline, about, filters, actions, bandSx }) {
  return (
    <Box component="aside" aria-label="Summary" sx={sidePanelSx}>
      <Box sx={{ ...sidePanelBandSx, ...(bandSx || {}) }}>
        <Typography variant="overline" sx={{ ...overlineSx, color: "rgba(255, 255, 255, 0.65)" }}>
          {overline}
        </Typography>
        <Typography
          data-testid="resource-panel-headline"
          sx={{ fontWeight: 700, fontSize: "1.15rem", lineHeight: 1.35, color: "#ffffff" }}
        >
          {headline}
        </Typography>
        {subline ? (
          <Typography
            data-testid="resource-panel-subline"
            variant="caption"
            sx={{ display: "block", mt: 0.25, color: "rgba(255, 255, 255, 0.7)", fontWeight: 600 }}
          >
            {subline}
          </Typography>
        ) : null}
      </Box>

      {about ? (
        <Box sx={{ px: 2.5, pt: 2, pb: 2.25 }}>
          <Typography variant="overline" sx={overlineSx}>
            About
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.55, mt: 0.5 }}>
            {about}
          </Typography>
        </Box>
      ) : null}

      {filters ? (
        <>
          <Divider />
          <Box sx={{ px: 2.5, pt: 2, pb: 2.25 }}>
            <Typography variant="overline" sx={overlineSx}>
              Filters
            </Typography>
            <Stack spacing={1.5} sx={{ mt: 1 }}>{filters}</Stack>
          </Box>
        </>
      ) : null}

      <Divider />
      <Box sx={{ px: 2.5, pt: 2, pb: 2.5 }}>
        <Stack spacing={1}>{actions}</Stack>
      </Box>
    </Box>
  );
}

/** The Mine / All toggle as a filter row of the panel. */
export function PanelScopeRow({ value, onChange }) {
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
      <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 600 }}>
        Show
      </Typography>
      <ListScopeToggle value={value} onChange={onChange} />
    </Stack>
  );
}

/** A small text button that resets the filters, shown only while one is set. */
export function PanelClearFilters({ onClick }) {
  return (
    <Button size="small" onClick={onClick} sx={{ textTransform: "none", alignSelf: "flex-start", fontWeight: 600 }}>
      Clear filters
    </Button>
  );
}

/** A full-width panel action: `primary` is the filled button, the rest are outlined. */
export function PanelButton({ primary = false, sx = {}, ...props }) {
  return (
    <Button
      fullWidth
      disableElevation
      variant={primary ? "contained" : "outlined"}
      sx={{
        textTransform: "none",
        fontWeight: primary ? 700 : 600,
        borderRadius: 2,
        ...(primary
          ? {}
          : {
              borderColor: "rgba(15, 23, 42, 0.16)",
              color: "text.primary",
              "&:hover": { borderColor: "rgba(15, 23, 42, 0.3)", bgcolor: "rgba(15, 23, 42, 0.03)" },
            }),
        ...sx,
      }}
      {...props}
    />
  );
}
